import React from 'react';
import { BookOpen, Clock, ArrowRight, Home, Mail, Phone, Construction } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';

const PublicationPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      
      {/* Page en construction */}
      <div className="relative min-h-screen flex items-center justify-center px-4 py-20">
        {/* Molecular Background Pattern */}
        <div className="absolute inset-0 overflow-hidden">
          <svg className="absolute w-full h-full" viewBox="0 0 1400 800" preserveAspectRatio="none">
            <defs>
              <radialGradient id="moleculePub" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#089556" stopOpacity="0.15"/>
                <stop offset="100%" stopColor="#089556" stopOpacity="0.05"/>
              </radialGradient>
              <radialGradient id="moleculePub2" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#07D1EB" stopOpacity="0.15"/>
                <stop offset="100%" stopColor="#07D1EB" stopOpacity="0.05"/>
              </radialGradient>
            </defs>
            
            {/* Animated Molecules */}
            <circle cx="100" cy="200" r="42" fill="url(#moleculePub)">
              <animate attributeName="cy" values="200;170;200" dur="5s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.3;0.7;0.3" dur="5s" repeatCount="indefinite"/>
            </circle>
            <circle cx="400" cy="120" r="38" fill="url(#moleculePub2)">
              <animate attributeName="cy" values="120;100;120" dur="7s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.4;0.8;0.4" dur="7s" repeatCount="indefinite"/>
            </circle>
            <circle cx="700" cy="320" r="35" fill="url(#moleculePub)">
              <animate attributeName="cy" values="320;300;320" dur="6s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.2;0.6;0.2" dur="6s" repeatCount="indefinite"/>
            </circle>
            <circle cx="1000" cy="180" r="40" fill="url(#moleculePub2)">
              <animate attributeName="cy" values="180;160;180" dur="8s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.3;0.7;0.3" dur="8s" repeatCount="indefinite"/>
            </circle>
            <circle cx="1300" cy="280" r="45" fill="url(#moleculePub)">
              <animate attributeName="cy" values="280;250;280" dur="4s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.4;0.8;0.4" dur="4s" repeatCount="indefinite"/>
            </circle>
            
            {/* Connecting Lines */}
            <line x1="100" y1="200" x2="400" y2="120" stroke="#089556" strokeWidth="2" opacity="0.3">
              <animate attributeName="opacity" values="0.3;0.7;0.3" dur="5s" repeatCount="indefinite"/>
            </line>
            <line x1="400" y1="120" x2="700" y2="320" stroke="#07D1EB" strokeWidth="2" opacity="0.4">
              <animate attributeName="opacity" values="0.4;0.8;0.4" dur="7s" repeatCount="indefinite"/>
            </line>
            <line x1="700" y1="320" x2="1000" y2="180" stroke="#089556" strokeWidth="2" opacity="0.3">
              <animate attributeName="opacity" values="0.3;0.6;0.3" dur="6s" repeatCount="indefinite"/>
            </line>
            <line x1="1000" y1="180" x2="1300" y2="280" stroke="#07D1EB" strokeWidth="2" opacity="0.4">
              <animate attributeName="opacity" values="0.4;0.8;0.4" dur="8s" repeatCount="indefinite"/>
            </line>
          </svg>
        </div>

        {/* Main Content */}
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* Construction Icon */}
          <div className="mb-8">
            <div className="relative inline-block">
              <div className="w-32 h-32 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-full flex items-center justify-center mb-6 mx-auto shadow-2xl">
                <Construction className="w-16 h-16 text-white animate-pulse" />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                <Clock className="w-4 h-4 text-yellow-800" />
              </div>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Page <span className="text-transparent bg-gradient-to-r from-[#089556] to-[#07D1EB] bg-clip-text">Publications</span>
          </h1>
          <h2 className="text-xl md:text-2xl text-gray-600 mb-8">
            En cours de construction
          </h2>

          {/* Description */}
          <div className="mb-12 max-w-2xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Nous développons une bibliothèque complète de nos publications, rapports et études réalisés par 
              <span className="font-semibold text-[#089556]"> Action Humanitaire Bénin</span>. 
              Bientôt, vous pourrez accéder à nos rapports annuels, guides pratiques et études d'impact.
            </p>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200">
              <div className="flex items-center justify-center mb-4">
                <BookOpen className="w-8 h-8 text-[#089556] mr-3" />
                <span className="text-lg font-semibold text-gray-800">Bientôt disponible</span>
              </div>
              <p className="text-gray-600">
                Cette page sera lancée prochainement avec toutes nos publications et ressources documentaires.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link 
              to="/"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#089556] to-[#07D1EB] text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              <Home className="w-5 h-5 mr-2" />
              Retour à l'accueil
            </Link>
            
            <Link 
              to="/contacts"
              className="inline-flex items-center px-8 py-4 bg-white text-[#089556] border-2 border-[#089556] rounded-lg font-medium hover:bg-[#089556] hover:text-white transition-all duration-200"
            >
              <Mail className="w-5 h-5 mr-2" />
              Nous contacter
            </Link>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link 
              to="/about"
              className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-200 group"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-lg flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                <ArrowRight className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">À propos de nous</h3>
              <p className="text-gray-600 text-sm">Découvrez notre mission et nos valeurs</p>
            </Link>

            <Link 
              to="/projets"
              className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-200 group"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-lg flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                <ArrowRight className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Nos projets</h3>
              <p className="text-gray-600 text-sm">Explorez nos actions humanitaires</p>
            </Link>

            <Link 
              to="/don"
              className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-200 group"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-[#089556] to-[#07D1EB] rounded-lg flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform">
                <ArrowRight className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Faire un don</h3>
              <p className="text-gray-600 text-sm">Soutenez nos actions dès maintenant</p>
            </Link>
          </div>

          {/* Contact Info */}
          <div className="mt-12 bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">En attendant, contactez-nous directement</h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#089556]" />
                <span className="text-gray-700">+229 01 55 07 81 81</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#089556]" />
                <span className="text-gray-700">contact@ahbenin.org</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PublicationPage;
